import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Grid, Typography, Divider, Button, LinearProgress } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { SectionGridContainer, PermissionedDisplay } from "../../components/Layout";
import { KeyValueDisplay } from "../../components/Typography";
import { RiderContext } from "../../context";
import { useTestIds } from "../../hooks/useTestIds";
import { ACCOUNT_STATUS, JUMIO_LEVELS_MAP } from "../../constants";
import Permissions from "../../permissions";

const useStyles = makeStyles((theme) => ({
  divider: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  infoRow: {
    margin: `${theme.spacing(1)}px 0`,
  },
  manageAccountButton: {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    borderRadius: 0,
  },
}));

export default function InfoWidget() {
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [ageVerificationStatus, setAgeVerificationStatus] = useState("");

  const testIds = useTestIds("rider-profile-info-widget");
  const theme = useTheme();
  const classes = useStyles(theme);
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { riderId } = useParams();
  const { Deleted } = ACCOUNT_STATUS;
  const { activeRider, setAccountStatus, accountStatus } = useContext(RiderContext);

  useEffect(() => {
    const currStatus = accountStatus?.split(" ")[0].trim();
    if (currStatus === "Suspended" || currStatus === "Banned") {
      const banSuspendReason = activeRider?.suspensions.at(-1).reason ?? "other";
      setReason(t(`Riders.Account.AccountStatus.${banSuspendReason}`));
    }
  }, [accountStatus, activeRider?.suspensions]);

  useEffect(() => {
    setLoading(true);
    const loadAccountStatus = async () => {
      try {
        setAccountStatus(activeRider?.suspensions);
      } catch (error) {
        console.error("Error fetching account status: ", error);
      }
    };
    if (activeRider) {
      loadAccountStatus();
      setLoading(false);
    }
  }, [activeRider, activeRider?.suspensions]);

  useEffect(() => {
    const manualDOBCheck = activeRider?.manual_dob_check;
    if (manualDOBCheck) {
      setAgeVerificationStatus(t("Fleet.Settings.General.manualDOBCheck"));
    } else {
      const jumioLevel = activeRider?.highest_completed_jumio_level;
      const verificationStatus = JUMIO_LEVELS_MAP[jumioLevel];
      setAgeVerificationStatus(verificationStatus);
    }
  }, [activeRider]);

  return (
    <SectionGridContainer spaceContents testId={testIds.wrapper}>
      <Grid item xs={12}>
        <Typography variant="h5" data-testid={testIds.title}>
          {t("Riders.RiderProfile.RiderInformation")}
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.divider}>
        <Divider />
      </Grid>
      {loading ? (
        <Grid item xs={12} style={{ padding: theme.spacing(2) }}>
          <LinearProgress color="secondary" data-testid="loading-bar" />
        </Grid>
      ) : (
        <Grid item xs={12} data-testid={testIds.content}>
          <KeyValueDisplay
            label={t("Riders.Account.AccountStatus.CurrentStatus")}
            text={accountStatus}
            className={classes.infoRow}
            testIds={{
              label: testIds.accountStatusLabel,
              text: testIds.accountStatusValue,
            }}
          />
          {(accountStatus?.split(" ")[0].trim() === "Suspended" ||
            accountStatus?.split(" ")[0].trim() === "Banned") && (
            <KeyValueDisplay
              label={t("Riders.Account.AccountStatus.Reason")}
              text={reason}
              className={classes.infoRow}
              testIds={{
                label: testIds.reasonLabel,
                text: testIds.reasonValue,
              }}
            />
          )}
          <KeyValueDisplay
            label={t("Riders.AgeVerificationDetails.AgeVerification")}
            text={ageVerificationStatus}
            className={classes.infoRow}
            testIds={{
              label: testIds.ageVerificationLabel,
              text: testIds.ageVerificationValue,
            }}
          />
          <KeyValueDisplay
            label={t("Riders.RiderProfile.Fields.Phone")}
            text={accountStatus === Deleted ? Deleted : activeRider?.phone_number}
            className={classes.infoRow}
            testIds={{
              label: testIds.phoneLabel,
              text: testIds.phoneValue,
            }}
          />
          <KeyValueDisplay
            label={t("Riders.RiderProfile.Fields.Email")}
            text={accountStatus === Deleted ? Deleted : activeRider?.email}
            className={classes.infoRow}
            testIds={{
              label: testIds.emailLabel,
              text: testIds.emailValue,
            }}
          />
          <KeyValueDisplay
            label={t("Riders.RiderProfile.Fields.RiderId")}
            text={activeRider?.id}
            className={classes.infoRow}
            testIds={{
              label: testIds.riderIdLabel,
              text: testIds.riderIdValue,
              clipboard: testIds.clipboardButton,
            }}
            copyToClipboard
          />
        </Grid>
      )}
      <Grid>
        <PermissionedDisplay permission={Permissions.writeRidersAdmin}>
          <Button
            data-testid={testIds.manageAccountButton}
            className={classes.manageAccountButton}
            onClick={() =>
              history.push(`/riders/${riderId}/account/manage`, { background: location })
            }
          >
            {t("Riders.Account.ManageAccount")}
          </Button>
        </PermissionedDisplay>
      </Grid>
    </SectionGridContainer>
  );
}
